/**
 * User Department Membership API Service
 * Handles multi-department membership operations
 */

import api from './api';
import { UserDepartmentMembership } from '../types/api';

export interface AddDepartmentMembershipRequest {
  departmentId: string;
  isPrimary?: boolean;
  role?: string | null;
}

export interface UpdateDepartmentMembershipRequest {
  isPrimary?: boolean;
  role?: string | null;
}

/**
 * Get user's department memberships
 * @param userId - User ID
 * @returns List of department memberships
 */
export const getUserDepartments = async (
  userId: string
): Promise<UserDepartmentMembership[]> => {
  const response = await api.get<{
    success: boolean;
    data: { departments: UserDepartmentMembership[] };
  }>(`/users/${userId}/departments`);

  return response.data.data.departments;
};

/**
 * Add department membership for a user
 * @param userId - User ID
 * @param data - Department membership data
 * @returns Created membership
 */
export const addUserDepartment = async (
  userId: string,
  data: AddDepartmentMembershipRequest
): Promise<UserDepartmentMembership> => {
  const response = await api.post<{
    success: boolean;
    data: { membership: UserDepartmentMembership };
    message: string;
  }>(`/users/${userId}/departments`, data);

  return response.data.data.membership;
};

/**
 * Update department membership (set primary or update role)
 * @param userId - User ID
 * @param departmentId - Department ID
 * @param data - Update data
 */
export const updateUserDepartment = async (
  userId: string,
  departmentId: string,
  data: UpdateDepartmentMembershipRequest
): Promise<void> => {
  await api.patch(`/users/${userId}/departments/${departmentId}`, data);
};

/**
 * Set department as primary for user
 * @param userId - User ID
 * @param departmentId - Department ID to set as primary
 */
export const setPrimaryDepartment = async (
  userId: string,
  departmentId: string
): Promise<void> => {
  await updateUserDepartment(userId, departmentId, { isPrimary: true });
};

export interface AffectedOKRsResult {
  count: number;
  okrs: Array<{ id: string; title: string }>;
}

/**
 * Check for OKRs affected by department removal
 * @param userId - User ID
 * @param departmentId - Department ID to check
 * @returns Information about affected OKRs
 */
export const checkAffectedOKRs = async (
  userId: string,
  departmentId: string
): Promise<AffectedOKRsResult> => {
  const response = await api.get<{
    success: boolean;
    data: AffectedOKRsResult;
  }>(`/users/${userId}/departments/${departmentId}/affected-okrs`);

  return response.data.data;
};

/**
 * Remove department membership
 * @param userId - User ID
 * @param departmentId - Department ID to remove
 * @param force - If true, reassign affected OKRs to primary department
 */
export const removeUserDepartment = async (
  userId: string,
  departmentId: string,
  force: boolean = false
): Promise<{ reassignedOKRs: number }> => {
  const response = await api.delete<{
    success: boolean;
    message: string;
    data: { reassignedOKRs: number };
  }>(`/users/${userId}/departments/${departmentId}${force ? '?force=true' : ''}`);

  return response.data.data;
};

/**
 * Update department role
 * @param userId - User ID
 * @param departmentId - Department ID
 * @param role - New role in this department
 */
export const updateDepartmentRole = async (
  userId: string,
  departmentId: string,
  role: string | null
): Promise<void> => {
  await updateUserDepartment(userId, departmentId, { role });
};
